import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TemperatureLog } from '../../types/temperatureLog';
import temperatureLogApiService from '../../services/temperatureLogApiService';

interface TemperatureLogState {
  freezerLogs: TemperatureLog[];
  ovenLogs: TemperatureLog[];
  fridgeLogs: TemperatureLog[];
  loading: boolean;
  error: string | null;
}

const initialState: TemperatureLogState = {
  freezerLogs: [],
  ovenLogs: [],
  fridgeLogs: [],
  loading: false,
  error: null,
};

const logKeyMap: Record<string, keyof Pick<TemperatureLogState, 'freezerLogs' | 'ovenLogs' | 'fridgeLogs'>> = {
  freezer: 'freezerLogs',
  oven: 'ovenLogs',
  fridge: 'fridgeLogs',
};

export const fetchTemperatureLogs = createAsyncThunk(
  'temperatureLogs/fetch',
  async (type: string) => {
    const logs = await temperatureLogApiService.fetchLogs(type);
    return { type, logs };
  }
);

export const createTemperatureLog = createAsyncThunk(
  'temperatureLogs/create',
  async ({ type, log }: { type: string; log: Partial<TemperatureLog> }) => {
    const created = await temperatureLogApiService.createLog(log);
    return { type, log: created };
  }
);

export const updateTemperatureLog = createAsyncThunk(
  'temperatureLogs/update',
  async ({ type, id, log }: { type: string; id: string; log: Partial<TemperatureLog> }) => {
    const updated = await temperatureLogApiService.updateLog(id, log);
    return { type, log: updated };
  }
);

export const deleteTemperatureLog = createAsyncThunk(
  'temperatureLogs/delete',
  async ({ type, id }: { type: string; id: string }) => {
    await temperatureLogApiService.deleteLog(id);
    return { type, id };
  }
);

const temperatureLogSlice = createSlice({
  name: 'temperatureLogs',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch logs
    builder
      .addCase(fetchTemperatureLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemperatureLogs.fulfilled, (state, action: PayloadAction<{ type: string; logs: TemperatureLog[] }>) => {
        state.loading = false;
        const key = logKeyMap[action.payload.type];
        if (key) {
          state[key] = action.payload.logs;
        }
      })
      .addCase(fetchTemperatureLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch temperature logs';
      });

    // Create log
    builder
      .addCase(createTemperatureLog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTemperatureLog.fulfilled, (state, action: PayloadAction<{ type: string; log: TemperatureLog }>) => {
        state.loading = false;
        const key = logKeyMap[action.payload.type];
        if (key) {
          state[key].unshift(action.payload.log);
        }
      })
      .addCase(createTemperatureLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create temperature log';
      });

    // Update log
    builder
      .addCase(updateTemperatureLog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTemperatureLog.fulfilled, (state, action: PayloadAction<{ type: string; log: TemperatureLog }>) => {
        state.loading = false;
        const key = logKeyMap[action.payload.type];
        if (key) {
          const index = state[key].findIndex((l) => l.id === action.payload.log.id);
          if (index !== -1) {
            state[key][index] = action.payload.log;
          }
        }
      })
      .addCase(updateTemperatureLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update temperature log';
      });

    // Delete log
    builder
      .addCase(deleteTemperatureLog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTemperatureLog.fulfilled, (state, action: PayloadAction<{ type: string; id: string }>) => {
        state.loading = false;
        const key = logKeyMap[action.payload.type];
        if (key) {
          state[key] = state[key].filter((l) => l.id !== action.payload.id);
        }
      })
      .addCase(deleteTemperatureLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete temperature log';
      });
  },
});

export const { clearError } = temperatureLogSlice.actions;
export default temperatureLogSlice.reducer;
